import { useState, useEffect } from "react";
import { Typography, Button, Modal, Box, Backdrop, Fade, Skeleton} from "@mui/material";
import { Translate, translate } from "react-i18nify";
import { PatientDetailText , PatientDetailTextInfo, PatientDetailTextWarning} from "./PatientDetailText";
import PatientDashboardCategory from "./PatientDashboardCategory";
import PatientDashboardCategoryWarning from "./PatientDashboardCategoryWarning";
import WarningIcon from '@mui/icons-material/Warning';
import TimeUtils from "../../utils/TimeUtils";
import { PatientService } from "../../services/DataService";

export const PatientTreatmentInfoComponent = (props) => {
  const [open, setOpen] = useState(false);
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  
  return(
    <Box sx={{ width:"100%", mt: 1, borderRadius: "15px", border: 1, borderColor: "primary.main", bgcolor: "primary.main", color: "primary.contrastText", p: 2}}>
      <Typography sx={{ mt: 1 }} variant="body">
        {<Translate value="TreatmentInformation" />}
      </Typography>
    <Box sx={{ width:"100%", display: "flex"}}>
    {props.treatmentPending ? (
      <>
      <Box sx={{ width:"45%", ml: "auto",}}>
        <Button
          sx={{ mt: 1.5, height: "30px", width:"max-content", alignSelf: "start", pl:1.5, fontSize:13, position:"absolute"}}
          color="warning"
          variant="contained"
          component="label"
          startIcon={<WarningIcon/>}
          onClick={handleOpen}
        >
          <Translate value="PendingInstallation" />
        </Button>
        <Modal
          open={open}
          onClose={handleClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
          backdrop: {
              timeout: 500,
          },
          }}
        >
          <Fade in={open}>
          <Box sx={{position: 'absolute',top: '50%',left: '50%',transform: 'translate(-50%, -50%)',width: "500px",borderRadius: "15px", borderColor: "warning.main", bgcolor: "warning.contrastText",boxShadow: 15}}>
            <Box sx={{width:"468px", borderColor: "warning.contrastText", bgcolor: "warning.main",borderRadius: "15px", borderBottom: 15, color: "warning.contrastText",
            p:2, display: 'flex', alignItems: 'center'}}>
            <WarningIcon sx={{height:"30px", width:"30px", mx:2}}/>
            <Typography variant="h5" component="h2">
                <Translate value="InstallationOfTreatmentPending" />
              </Typography>
            </Box>
            <Box sx={{p:2}}>
              <Typography sx={{color: "#000000", whiteSpace:"pre-line" }}>
                <Translate value="CurrentTreatmentOnDeviceDescription" /> 
              </Typography>
              <Typography variant="h6" component="h2" sx={{mt:2, mb:-2, color: "#000000"}}>
                  <Translate value="CurrentTreatmentOnDevice" />
              </Typography>
            </Box>
            <Box sx={{p:2}}>
            <PatientTreatmentInfo patientId={props.patientId} treatment={props.previousTreatment} treatmentPending={false} deviceCurrentTreatment={props.deviceCurrentTreatment} treatmentPrescribed={false}/>
            </Box>
          </Box>
          </Fade>
        </Modal>
      </Box>
      </>
      ) : (
        <Box sx={{ width:"55%"}}>
        </Box>
      )}
      </Box>

      <PatientTreatmentInfo patientId={props.patientId} treatment={props.treatment} treatmentPending={props.treatmentPending} deviceCurrentTreatment={props.deviceCurrentTreatment} treatmentPrescribed={props.treatmentPrescribed}/>
    </Box>  
  )
}
  
const PatientTreatmentInfo = (props) => {
  const [treatmentPrescribed, setTreatmentPrescribed] = useState(null);
  const [deviceCurrentTreatmentTimestamp, setDeviceCurrentTreatmentTimestamp] = useState(null);
  const [treatment, setTreatment] = useState({
    id: null,
    name: null,
    spO2Minimum: 0,
    spO2Maximum: 0,
    lowO2ShortTerm: undefined,
    lowO2LongTerm: undefined,
    highO2ShortTerm: 0,
    highO2LongTerm: 0,
    durLod1: 0,
    OAD: 0,
    flowResponse: "",
    oxygenPrescription: 0,
    shortTerm: "",
    iBackupFlow: 0
  });

  useEffect(() => {
    if(props.treatment !== null && treatment.id == null){
      setTreatment({
        id: props.treatment?.id,
        name: props.treatment?.name,
        oxygenPrescription: props.treatment?.minutesPrescribed / 60,
        spO2Minimum: props.treatment?.values.find((item) => item.code === "LSpO2")?.value,
        spO2Maximum: props.treatment?.values.find((item) => item.code === "HSpO2")?.value,
        lowO2ShortTerm: props.treatment?.values.find((item) => item.code === "LO2ST")?.value,
        lowO2LongTerm: props.treatment?.values.find((item) => item.code === "LO2LT")?.value,
        highO2ShortTerm: props.treatment?.values.find((item) => item.code === "HO2ST")?.value,
        highO2LongTerm: props.treatment?.values.find((item) => item.code === "HO2LT")?.value,
        durLod1: props.treatment?.values.find((item) => item.code === "DurLod1")?.value,
        OAD: props.treatment?.values.find((item) => item.code === "OAD")?.value,
        flowResponse: 
          props.treatment?.values.find((item) => item.code === "Ki")?.value == 150 ? "Very fast" : 
          props.treatment?.values.find((item) => item.code === "Ki")?.value == 100 ? "Fast" : 
          props.treatment?.values.find((item) => item.code === "Ki")?.value == 25 ? "Normal" : 
          "Custom (" + props.treatment?.values.find((item) => item.code === "Ki")?.value + ")",
        shortTerm: 
          props.treatment?.values.find((item) => item.code === "OAD")?.value == 15 * 60 ? "15 min/hour" : 
          props.treatment?.values.find((item) => item.code === "OAD")?.value == 30 * 60 ? "30 min/hour" : 
          props.treatment?.values.find((item) => item.code === "OAD")?.value == 45 * 60 ? "45 min/hour" : 
          props.treatment?.values.find((item) => item.code === "OAD")?.value == 61 * 60 ? "Always" : "",
        iBackupFlow: props.treatment?.values.find((item) => item.code === "IBF")?.value ? props.treatment?.values.find((item) => item.code === "IBF")?.value : "" //Ask if no than "" or 0
      })
    }
    if(props.treatment && props.patientId){ //This call is in its own useEffect because otherwise it breaks the app. It is not clear why...
      (async () => {
        await PatientService.getTreatmentPrescribedTimeByTreatmentId(props.patientId, props.treatment.id)
        .then((response) => {
          setTreatmentPrescribed(response.data)
        }).catch((error) => {
          console.error("Error getting treatment prescribed time by treatment id: ", error)
        })
      })();
    }
  }, [props.treatment]);

  useEffect(() => {
    if(props.deviceCurrentTreatment === "Unknown"){
      setDeviceCurrentTreatmentTimestamp("-")
    }else{
      setDeviceCurrentTreatmentTimestamp(TimeUtils.utcToLocal(props.deviceCurrentTreatment?.timestamp))
    }
  }, [props.deviceCurrentTreatment])

    return (
      <Box>
        <Box sx={{mt: 1}}>
          {treatment.name ? (
            props.treatmentPending === true ? (
            <PatientDashboardCategoryWarning // if "pending" then color treatment name as warning
             title={treatment.name}
            ></PatientDashboardCategoryWarning>
            ) : (
            <PatientDashboardCategory // if "pending" then color treatment name as warning
              title={treatment.name}
            ></PatientDashboardCategory>
            )) : (
              <Skeleton variant="rounded" width={"50%"} height={50} />
            )
          }
        </Box>

        <Box sx={{mt: 2}}>
          {
            (props.treatmentPending !== null && treatmentPrescribed !== null) ? (
              <>
              <PatientDetailText
              label={<Translate value="Prescribed" />}
              value={TimeUtils.utcToLocal(treatmentPrescribed)}
              ></PatientDetailText>
               { props.treatmentPending === true ? 
                  (
                    <PatientDetailTextWarning
                      label={<Translate value="Installed" />}
                      value={<Translate value="Pending" />}
                    ></PatientDetailTextWarning>
                  ) : (
                    <PatientDetailText
                      label={<Translate value="Installed" />}
                      value={deviceCurrentTreatmentTimestamp}
                    ></PatientDetailText> 
                  )
                }
              </> 
            ) 
            : 
            (
              <Skeleton variant="rounded" width={"90%"} height={48} />
            )
          }
        </Box>
        <Box sx={{mt: 3}}>
        {treatment.id ? (
          <>
          <PatientDetailText
            label={<Translate value="TreatmentId" />}
            value={treatment.id}
          ></PatientDetailText>
          <PatientDetailTextInfo
            // label={<Translate value="Lod1" />}
            label={<Translate value="AnalysingPhase" />}
            value={treatment.durLod1 == 300 ? translate("Yes") : translate("No")}
            info={<Translate value="AnalysingPhaseExplanationPatientPage" />}
            
          ></PatientDetailTextInfo>
          
          <PatientDetailTextInfo
            label={<Translate value="FlowResponse" />}
            value={treatment.flowResponse}
            info = {<Translate value="FlowResponseExplanation" />}
          ></PatientDetailTextInfo>

          <PatientDetailTextInfo
            // label={<Translate value="OxygenPrescription" />}
            label={<Translate value="O2ComplianceGoal" />}
            value={treatment.oxygenPrescription + " " + translate("hours/day")}
            info = {<Translate value="OxygenComplianceGoalExplanationPatientPage" />}
          ></PatientDetailTextInfo>
          <PatientDetailText
            label={<Translate value="SpO2Range" />}
            value={treatment.spO2Minimum + " - " + treatment.spO2Maximum + " %"}
          ></PatientDetailText>
          </>
        ) : (
          <Skeleton variant="rounded" animation="pulse" height={120}  width={"90%"} /> // 
        )
        }
        </Box>

        <Box sx={{mt: 2, mb:1}}>
          {treatment.lowO2LongTerm || treatment.lowO2ShortTerm >= 0 ? (
            <>
              <Typography variant="h6">
              <Translate value="O2FlowRanges" />
              </Typography>
              <PatientDetailText
                label={<Translate value="WithoutPO" />}
                value={treatment.lowO2LongTerm + " - " + treatment.highO2LongTerm + " " + translate("l/min")}
              ></PatientDetailText>
    
              <PatientDetailText
                label={<Translate value="WithPO" />}
                value={treatment.lowO2ShortTerm + " - " + treatment.highO2ShortTerm + " " +translate("l/min")}
              ></PatientDetailText>
            </>
          ) : (
            <Skeleton variant="rounded" animation="pulse" height={70}  width={"90%"} /> // 
          )}
        </Box>

        {treatment.shortTerm ? (
        <>
        <Box sx={{mb:-0.75}}>
          <Typography variant="h7">
            <Translate value="AllowedTimeWithin" />
          </Typography>
        </Box>
        
        <PatientDetailTextInfo
          // label={<Translate value="ShortTermLength" />}
          label={treatment.lowO2ShortTerm + " - " + treatment.lowO2LongTerm + " " + translate("and") + " " + treatment.highO2LongTerm + " - " + treatment.highO2ShortTerm + " " + translate("l/min")}
          value={treatment.shortTerm}
          info = {<Translate value="AllowedTimeWithinExplanation" />}
        ></PatientDetailTextInfo>
        <Box sx={{mt:-0.75}}>
          <Typography variant="h7">
            <Translate value="withPulseOximeterOn" />
          </Typography>
        </Box>
        <Box sx={{ mt:0.5, }}>
          <PatientDetailTextInfo
            label={<Translate value="IndependentBackupFlow" />}
            value={treatment.iBackupFlow ? treatment.iBackupFlow + " " +translate("l/min") : translate("None")}
            info={<Translate value="IBackupFlow.Explainer.PatientPage" />}
          ></PatientDetailTextInfo>
        </Box>
        </>
          ) : (
            <Skeleton variant="rounded" animation="pulse" height={60}  width={"90%"} /> // 
          )}
      </Box>
    );
};

export default PatientTreatmentInfo;
